/**
 * Aura AGM — 폼값 → 템플릿 [[변수]] 최종 변환.
 *  · DIRECTORS/SIGNATURES 반복부: agm_dir_{i}_name → directors[] ({director_name}).
 *  · DDF 이해관계 반복부: own/family/deemed/corporate + other_interests 를 'yes' 플래그 → 행 배열로.
 *  · 체크박스(company_type/activity/solvency/*_location, declare_*)는 문자열 그대로(백엔드 조건식이 비교).
 *  · 날짜는 ISO(YYYY-MM-DD) 그대로 — 백엔드 치환기가 세션 형식으로 변환.
 */
function toBool(v: unknown): boolean {
  const s = String(v ?? '').trim().toLowerCase();
  return s === 'yes' || s === 'true' || s === '1' || s === 'y';
}

/** company_type 코드 → Letter 의 [[company_type_name]] 표시 라벨. */
const COMPANY_TYPE_LABEL: Record<string, string> = {
  epc_shares: 'Exempt Private Company Limited By Shares',
  private_shares: 'Private Company Limited By Shares',
  public_guarantee: 'Public Company Limited By Guarantee',
  public_shares: 'Public Company Limited By Shares',
  unlimited_epc: 'Unlimited Exempt Private Company',
  unlimited_private: 'Unlimited Private Company',
  unlimited_public: 'Unlimited Public Company',
};

/** FYE(ISO) → 그 회계연도 시작일(ISO) = 전년도 FYE 다음 날. (2/29 FYE 는 전년 2/28 기준) ISO 아니면 '' */
function fyStartFromEnd(v: unknown): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? '').trim());
  if (!m) return '';
  const y = Number(m[1]) - 1, mo = Number(m[2]), d = Number(m[3]);
  const dim = new Date(Date.UTC(y, mo, 0)).getUTCDate(); // 전년도 그 달의 말일
  const dimCur = new Date(Date.UTC(y + 1, mo, 0)).getUTCDate(); // FYE 해의 그 달 말일
  // 월말 FYE(예: 2/28, 6/30)는 전년도도 그 달 말일로 본다 — 2025-02-28 → 전년 2024-02-29 → 시작 2024-03-01
  const prevDay = d >= dimCur ? dim : Math.min(d, dim);
  const dt = new Date(Date.UTC(y, mo - 1, prevDay + 1));
  return dt.toISOString().slice(0, 10);
}

export function normalizeAgmForWord(input: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = { ...input };

  // ── Letter: company_type_name (선택 유형 라벨) 파생 ──
  if (!String(out.company_type_name ?? '').trim()) {
    const ct = String(out.company_type ?? '').trim();
    if (COMPANY_TYPE_LABEL[ct]) out.company_type_name = COMPANY_TYPE_LABEL[ct];
  }
  // ── Letter: Company Type Status 별칭 — 템플릿 버전에 따라 company_activity / company_status 키가 갈릴 수 있어 둘 다 방출.
  {
    const act = String(out.company_activity ?? out.company_status ?? '').trim();
    if (act) { out.company_activity = act; out.company_status = act; }
  }

  // ── DIRECTORS / SIGNATURES 반복부 ─────────────────────────────
  if (!Array.isArray(out.directors)) {
    const count = parseInt(String(out.agm_director_count ?? '').trim(), 10);
    const rows: Array<{ director_name: string }> = [];
    const max = Number.isFinite(count) && count > 0 ? count : 0;
    for (let i = 1; i <= max; i++) {
      const name = String(out[`agm_dir_${i}_name`] ?? '').trim();
      if (name) rows.push({ director_name: name });
    }
    // count 키가 없어도 agm_dir_*_name 이 있으면 수집
    if (!max) {
      for (let i = 1; i <= 20; i++) {
        const name = String(out[`agm_dir_${i}_name`] ?? '').trim();
        if (name) rows.push({ director_name: name });
      }
    }
    if (rows.length) out.directors = rows;
  }

  // ── MEMBERS 반복부 (agm_member_{i}_name + 법인 대표자) ─────────
  //   member_name(1번째 줄) + member_address(2번째 줄: 법인 대표자 캡션). 백엔드 멤버 셀이 두 줄로 렌더하므로
  //   docx 템플릿(멤버 루프 [[if members]][[member_name]])은 그대로 재사용 — 수정 불필요.
  if (!Array.isArray(out.members)) {
    const mcount = parseInt(String(out.agm_member_count ?? '').trim(), 10);
    const rows: Array<{ member_name: string }> = [];
    const max = Number.isFinite(mcount) && mcount > 0 ? mcount : 30;
    for (let i = 1; i <= max; i++) {
      const name = String(out[`agm_member_${i}_name`] ?? '').trim();
      if (!name) continue;
      const rep = String(out[`agm_member_${i}_representative_name`] ?? '').trim();
      const isCorp = toBool(out[`agm_member_${i}_is_corporate`]);
      // 대표자 있는 멤버는 **한 줄** 캡션 그대로: "{대표법인} (Duly Appointed Representative of {멤버})".
      let display = name;
      if (rep) display = `${rep} (Duly Appointed Representative of ${name})`;
      else if (isCorp) display = `(Duly Appointed Representative of ${name})`;
      rows.push({ member_name: display });
    }
    if (rows.length) out.members = rows;
  }

  // ── DDF 이해관계 반복 섹션 ────────────────────────────────────
  //   4개 구분(own_name/family/deemed/corporate)은 각자 **구분된 키**(own_name_registered_holder …)를
  //   써서 서로 다른 보유자·수량을 기재할 수 있다. 여기서 각 섹션별 루프 행 1개로 매핑.
  const SECTION_PFX: Record<string, string> = {
    own_name_interests: 'own_name',
    family_interests: 'family',
    deemed_interests: 'deemed',
    corporate_interests: 'corporate',
  };
  for (const [sec, pfx] of Object.entries(SECTION_PFX)) {
    if (Array.isArray(out[sec])) continue;
    out[sec] = toBool(out[sec]) ? [{
      registered_holder: out[`${pfx}_registered_holder`] || '',
      interest_description: out[`${pfx}_interest_description`] || '',
      amount_at_appointment: out[`${pfx}_amount_at_appointment`] || '',
      amount_at_end: out[`${pfx}_amount_at_end`] || '',
      date_price_details: out[`${pfx}_date_price_details`] || '',
    }] : [];
  }
  if (!Array.isArray(out.other_interests)) {
    // 값이 비어 있으면 화면 기본값(체크)과 같게 본다 — 명시적으로 해제('no')한 경우만 표 행을 뺀다.
    const oi = String(out.other_interests ?? '').trim();
    out.other_interests = (oi === '' || toBool(oi))
      ? [{ corporation_name: out.corporation_name || '', interest_nature: out.interest_nature || '' }]
      : [];
  }

  // ── 회사 공통 별칭·파생값 (2026-10 Aura 전수조사) ─────────────────────
  //   · Notice for Controller 템플릿 일부(CAU004)는 [[company_secretary_name]] — 자동채움 키는 secretary_name.
  if (!String(out.company_secretary_name ?? '').trim() && String(out.secretary_name ?? '').trim()) {
    out.company_secretary_name = out.secretary_name;
  }
  //   · DDF [[financial_year_start_date]] — 엔티티에 시작일이 없으면 FYE 에서 계산(전년 FYE 다음 날).
  if (!String(out.financial_year_start_date ?? '').trim()) {
    const fys = fyStartFromEnd(out.financial_year_end);
    if (fys) out.financial_year_start_date = fys;
  }

  return out;
}
